import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProgressBar from '../../components/ProgressBar';
import { useSelector, useDispatch } from 'react-redux';
import PatientProgramSetup from './PatientProgramSetup';
import DeviceEnrollment from './DeviceEnrollment';
import QuestionnaireEnrollment from './QuestionnaireEnrollment';
import ContentEnrollment from './ContentEnrollment';
import EnrollmentRules from './EnrollmentRules';
import { setModalitiesState } from './PatientModalitiesSlice';
import { setEnrollmentFormValue } from './PatientEnrollmentSlice';
import useAxios from '../../services/AxiosConfig';
import styles from '../ManagementPages.module.css';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { setListValue } from './EnrollmentListSlicer';
import ReviewEnrollment from './ReviewEnrollment';
import { checkModalityThresholds, isInvalidBpMinuteFrequency } from '../../utils/programUtils';
import Logger from '../../services/Logger';
import { LogElements, LogAction } from '../../services/LogElements';

const EnrollPatient = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const data = useSelector((state) => state.enrollPatientProgram);
    const updatingProgram = data.updating;
    const addingProgram = data.adding;
    const reenrollingProgram = data.reenrolling;
    const { modalities, notifications, modalityFrequencies } = useSelector((state) => state.patientEnrollmentModalities);
    const templateModalities = data.templateModalities;
    const userId = useSelector((state) => state.userInfo.id);
    const patientId = useSelector((state) => state.enrollPatientProgram.patient.id);
    const savedModalityValues = useSelector((state) => state.savedPatientModalities.patientRecentModalities[patientId]) ?? {};
    const patientEnrollmentsList = useSelector((state) => state.enrollmentLists.previousEnrollments);
    const defaultModalities = useSelector((state)=>state.dropdowns.modality.values);

    useEffect(() => {
        fetchData();
    }, []);

    const formatPatientsDropdown = (patientData) => {
        let uniqueIds = new Set();
        const formatted = [];
        patientData.forEach((patient) => {
            if (!uniqueIds.has(patient.id)) {
                uniqueIds.add(patient.id);
                formatted.push({
                    label: patient.firstName + ' ' + patient.lastName + ` (${patient.countryCode}${patient.phoneNumber})`,
                    value: { firstName: patient.firstName, lastName: patient.lastName, id: patient.id }
                })
            }
        });
        return formatted;
    };

    const formatPrograms = (programData) => {
        const formatted = programData.map((program) => ({
            label: program.name,
            value: program
        }));
        const addingNewProgram = { label: "Create Custom Program", value: { name: "Create Custom Program", id: 0 } };
        return [addingNewProgram, ...formatted];
    };

    const fetchData = async () => {
        httpService.get(`content/list/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    dispatch(setListValue({ key: "content", value: responseData.data.data }));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });

        httpService.get(`enrollment/list/patient/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const formattedPatients = formatPatientsDropdown(responseData.data.data);
                    dispatch(setListValue({ key: "patients", value: formattedPatients }));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });

        httpService.get(`questionnaire/list/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    dispatch(setListValue({ key: "questionnaires", value: responseData.data.data }));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });

        httpService.get(`device/group/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const deviceData = responseData.data.data;
                    for (const device of deviceData) {
                        device.listLabel = `${device.model} - ${device.manufacturer}`
                    }
                    dispatch(setListValue({key:"devices", value:deviceData}));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });

        httpService.get(`careprogram/list/${userId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const formattedPrograms = formatPrograms(responseData.data.data);
                    dispatch(setListValue({ key: "programs", value: formattedPrograms }));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const [errorMessages, setErrorMessages] = useState({
        program: {
            message: "Program Selection and Duration are required to proceed.",
            showing: false,
            patient: {
                showing: false,
                message: "Please choose a patient to enroll."
            },
            program: {
                showing: false,
                message: "Please select or create a program."
            },
            programName: {
                showing: false,
                message: "Please create a program name."
            },
            duration: {
                showing: false,
                message: "Please enter a valid duration for the program.",
            },
            group: {
                showing: false,
                message: "Please select teams.",
            }
        },
        device: {
            message: "All devices are required to have a valid serial number, start time, and frequency",
            showing: false,
            modalityMessage: "Please make sure upper limit > lower limit and target value is in between values",
            modalityShowing: false,
            modalityErrors: [],
            deviceIdErrors: [],
            perinModalityErrors: [],
            serialNumberMessage: "Please enter a valid serial number for all devices",
            serialNumberShowing: false
        },
        rules: {
            repeatNotificationShowing: false,
            repeatNotificationMessage: "Please choose one notification rule per modality",
            repeatModalityFrequencyShowing: false,
            repeatModalityFrequencyMessage: "Please choose one modality frequency rule per modality",
            notificationSelectionShowing: false,
            modFrequencySelectionShowing: false,
        },
        questionnaire: { message: "All questionnaires are required to have a valid start time and frequency", showing: false },
        content: {
            contentMessage: "All content are required to have a valid start time and frequency",
            contentShowing: false,
            programMessage: "At least one device, questionnaire, or content is required to enroll a patient in a program",
            programShowing: false,
        },
    });

    const devices = useSelector((state) => state.enrollmentLists.devices);

    useEffect(() => {
        // check if we're updating a program and save the modalities for device selection to the slice
        if (updatingProgram || reenrollingProgram) {
            dispatch(setModalitiesState({ programDevices: data.originalDevices, allDevices: devices, modalityValues: savedModalityValues }));
        }
        if (addingProgram || updatingProgram || reenrollingProgram) {
            const groups = data.enrollment.groups;
            const organizationId = data.hasOwnProperty("organizationId") ? data.organizationId : false;
            const newGroups = groups.map((group) => {
                return organizationId ? { ...group, organizationId: organizationId } : group
            });
            dispatch(setEnrollmentFormValue({key:"groups", value:newGroups}));
        }
    }, [devices]);

    const [page, setPage] = useState(0);

    const PageDisplay = () => {
        if (page === 0) {
            return <PatientProgramSetup errors={errorMessages.program} />
        } else if (page === 1) {
            return <DeviceEnrollment errors={errorMessages.device} />
        } else if (page === 2) {
            return <EnrollmentRules errors={errorMessages.rules} />
        } else if (page === 3) {
            return <QuestionnaireEnrollment errors={errorMessages.questionnaire} />
        } else if (page === 4) {
            return <ContentEnrollment errors={errorMessages.content} />
        } else if (page === 5) {
            return <ReviewEnrollment />
        } else {
            navigate('/patient-enrollment');
        }
    };

    const getPageName = (step) => {
        if (step === 0) {
            return "Program Setup";
        } else if (step === 1) {
            return "Device Setup";
        } else if (step === 2) {
            return "Enrollment Rules";
        } else if (step === 3) {
            return "Questionnaire Setup";
        } else if (step === 4) {
            return "Content Setup";
        } else if (step === 5) {
            return "Review Enrollment";
        }
    };

    const ProgressBarNavigation = (status) => {
        const pages = {
            "Program Setup": 0,
            'Device Setup': 1,
            'Enrollment Rules': 2,
            "Questionnaire Setup": 3,
            "Content Setup": 4,
            "Review Enrollment": 5
        };
        if (pages[status] || pages[status] === 0) {
            if (checkPageValidity()) {
                return;
            };
            setPage(pages[status]);
        } else {
            return;
        }
    };

    const nextPage = () => {
        setPage(page + 1);
    };

    const prevPage = () => {
        if (page !== 0) {
            setPage(page - 1);
        }
    };

    const checkPageValidity = () => {
        let tempError = { ...errorMessages };
        if (page === 0) {
            let patientInvalid = false;
            let programInvalid = false;
            let durationInvalid = false;
            let groupInvalid = false;
            if (data.patient.id === "") {
                patientInvalid = true;
            } else if (data.enrollment.groups.length === 0) {
                groupInvalid = true;
                const teamsSelect = document.getElementById('teams-select');
                if (teamsSelect) {
                    teamsSelect.classList.add(styles['error-input']);
                }
            } else if (data.name === "") {
                programInvalid = true;
                // Add a class to the program selection field
                const programSelect = document.getElementById('program-select');
                if (programSelect) {
                    programSelect.classList.add(styles['error-input']);
                }
                // return true; // Indicates that there's an error
            } else if (data.duration === "" || data.duration === 0) {
                durationInvalid = true;
            }
            // check for previous/current enrollments by same name if not updating
            else if (patientEnrollmentsList[data.patient.id] && !updatingProgram) {
                const enrollments = patientEnrollmentsList[data.patient.id];
                if (enrollments["current"].find((name)=>name===data.name)) {
                    dispatch(
                        showAlert({
                            header: "Error",
                            message: "Patient is currently enrolled in a program by the same name",
                            type: 'danger'
                        })
                    );
                    return true;
                }
            }
            tempError.program.patient.showing = patientInvalid;
            tempError.program.program.showing = programInvalid;
            tempError.program.duration.showing = durationInvalid;
            tempError.program.group.showing = groupInvalid;
            tempError.content.programShowing = false;
            tempError.program.showing = patientInvalid || programInvalid || durationInvalid || groupInvalid;
            setErrorMessages(tempError);
            return patientInvalid || programInvalid || durationInvalid || groupInvalid;
        } else if (page === 1) {
            let invalid = false;
            let modalitiesInvalid = false;
            let modalitiesListInvalid = [];
            let perinModalitiesListInvalid = [];
            let devicesListInvalid = [];
            let serialNumberInvalid = false;
            if (data.devices.length >= 1) {
                let countValid = 0;
                data.devices.forEach((device) => {

                    // check if all devices have a start time and a frequency/isContinuous
                    if (device.deviceId === 1) {
                        // have to check each modalities start time 
                        const perinModalities = modalities.filter((mod)=> mod.deviceId === 1);
                        let allPerinModalitiesValid = perinModalities.length > 0;
                        perinModalities.forEach((mod) => {
                            if (mod.startTime !== "" && mod.frequency >= 0 && mod.frequency !== "") {
                                if (
                                    isInvalidBpMinuteFrequency(mod) ||
                                    (mod.interval === "Day" && mod.frequency > 365) ||
                                    (mod.interval === "Min" && mod.frequency > 60) ||
                                    (mod.interval === "Hour" && mod.frequency > 24)
                                ) {
                                    perinModalitiesListInvalid.push(mod.deviceModalityId)
                                    allPerinModalitiesValid = false;
                                }
                            } else {
                                perinModalitiesListInvalid.push(mod.deviceModalityId)
                                allPerinModalitiesValid = false;
                            }
                        });

                        if (allPerinModalitiesValid) {
                            countValid++;
                        }
                        
                    } else {
                        // check if all other devices have a valid start time and a frequency/isContinuous
                        if (device.startTime !== "" && (device.frequency >= 0 || device.isContinuous) && device.serialNumber !== "") {
                            if (!device.isContinous) {
                                if (device.interval === "Day") {
                                    if (device.frequency > 365) {
                                        return;
                                    }
                                } else if (device.interval === "Min") {
                                    if (device.frequency > 60) {
                                        return;
                                    }
                                } else if (device.interval === "Hour") {
                                    if (device.frequency > 24) {
                                        return;
                                    }
                                }
                            }
                            countValid++;
                        }
                    }

                    if (device.serialNumber === "") {
                        serialNumberInvalid = true;
                    }
            
                })
                if (countValid !== data.devices.length) {
                    // devices are missing information
                    invalid = true;
                };
                modalities.forEach((mod) => {
                    const currModInvalid = checkModalityThresholds(mod, defaultModalities, devices);
                    if (currModInvalid) {
                        modalitiesInvalid = true;
                        modalitiesListInvalid.push(mod.deviceModalityId)
                        if (!devicesListInvalid.some(id => id === mod.deviceId)) {
                            devicesListInvalid.push(mod.deviceId);
                        }
                    }
                });
            }
            tempError.device.modalityShowing = modalitiesInvalid;
            tempError.device.modalityErrors = modalitiesListInvalid;
            tempError.device.deviceIdErrors = devicesListInvalid;
            tempError.device.perinModalityErrors = perinModalitiesListInvalid;
            tempError.device.showing = invalid || serialNumberInvalid || modalitiesInvalid;
            tempError.device.serialNumberShowing = serialNumberInvalid;
            tempError.content.programShowing = false;
            setErrorMessages(tempError);
            return invalid || modalitiesInvalid || serialNumberInvalid;
        } else if (page === 2) {
            let notificationInvalid = false;
            let notificationSelectionInvalid = false;
            let modalityFrequencyInvalid = false;
            let modFrequencySelectionInvalid = false;
            const notificationUniqueCount = new Set(notifications.map((not) => not.modalityId)).size;
            if (notificationUniqueCount !== notifications.length) {
                notificationInvalid = true;
            }
            if (notifications.some((not) => not.modalityId === 0)) {
                notificationSelectionInvalid = true;
            }
            const modalityFrequenciesUniqueCount = new Set(modalityFrequencies.map((not) => not.modalityId)).size;
            if (modalityFrequenciesUniqueCount !== modalityFrequencies.length) {
                modalityFrequencyInvalid = true;
            }
            if (modalityFrequencies.some((not) => not.modalityId === 0)) {
                modFrequencySelectionInvalid = true;
            }
            tempError.rules.repeatNotificationShowing = notificationInvalid;
            tempError.rules.repeatModalityFrequencyShowing = modalityFrequencyInvalid;
            tempError.rules.notificationSelectionShowing = notificationSelectionInvalid;
            tempError.rules.modFrequencySelectionShowing = modFrequencySelectionInvalid;
            tempError.content.programShowing = false;
            setErrorMessages(tempError);
            return notificationInvalid || modalityFrequencyInvalid || modFrequencySelectionInvalid || notificationSelectionInvalid ;
        } else if (page === 3) {
            let invalid = false;
            if (data.questionnaires.length >= 1) {
                let countValid = 0;
                data.questionnaires.forEach((q) => {
                    // check if all devices have a valid start time and frequency
                    if (q.startTime !== "" && q.frequency >= 1) {
                        if (q.interval === "Day") {
                            if (q.frequency > 365) {
                                return;
                            }
                        } else if (q.interval === "Min") {
                            if (q.frequency > 60) {
                                return;
                            }
                        } else if (q.interval === "Hour") {
                            if (q.frequency > 24) {
                                return;
                            }
                        }
                        countValid++;
                    };
                })
                if (countValid !== data.questionnaires.length) {
                    // devices are missing information
                    invalid = true;
                };
            }
            tempError.questionnaire.showing = invalid;
            tempError.content.programShowing = false;
            setErrorMessages(tempError);
            return invalid;
        }
        else if (page === 4) {
            let contentInvalid = false;
            let programInvalid = false;
            if (data.contents.length >= 1) {
                let countValid = 0;
                data.contents.forEach((content) => {
                    // check if all devices have a start time and a frequency/isContinuous
                    if (content.startTime !== "" && content.frequency >= 1) {
                        if (content.interval === "Day") {
                            if (content.frequency > 365) {
                                return;
                            }
                        } else if (content.interval === "Min") {
                            if (content.frequency > 60) {
                                return;
                            }
                        } else if (content.interval === "Hour") {
                            if (content.frequency > 24) {
                                return;
                            }
                        }
                        countValid++;
                    };
                })
                if (countValid !== data.contents.length) {
                    // devices are missing information
                    contentInvalid = true;
                };
            } if (data.devices.length === 0 && data.questionnaires.length === 0 && data.contents.length === 0) {
                // check if there's at least one selected device, questionnaire, or content
                programInvalid = true;
            }
            tempError.content.contentShowing = contentInvalid;
            tempError.content.programShowing = programInvalid;
            setErrorMessages(tempError);
            return contentInvalid || programInvalid;
        }
        return false;
    };

    const formatProgramData = () => {
        const devices = data.devices;
        const deviceNotes = data.deviceNotes;
        const contents = data.contents;
        const contentNotes = data.contentNotes;
        const questionnaires = data.questionnaires;
        const questionnaireNotes = data.questionnaireNotes;
        const hubs = data.hubs;
        const formattedGroups = data.enrollment.groups.map((group) => {
            return {
                groupId: group.groupId,
                organizationId: group.organizationId,
                subGroupId: group.subGroupId,
                teamId: group.teamId,
                accountTeamPatientId: group.accountTeamPatientId,
            };
        });

        let newDevices = [];
        if (devices.length >= 1) {
            // same process as creation but for each modality, check if it was already there and add accountProgramDeviceId to device, otherwise remove accountProgramDeviceId
            devices.forEach((device) => {
                let tempDevices = [];
                modalities.forEach((mod) => {
                    if (mod.deviceId === device.deviceId) {
                        let tempDevice = { ...device };
                        tempDevice['deviceModalityId'] = mod.deviceModalityId;
                        tempDevice['modalityName'] = mod.modalityName;
                        tempDevice['modalityType'] = mod.modalityType;
                        tempDevice['modalityTypeDisplayName'] = mod.modalityTypeDisplayName;
                        tempDevice['modalityId'] = mod.modalityId;
                        tempDevice['upperLimit'] = mod.upperLimit;
                        tempDevice['lowerLimit'] = mod.lowerLimit;
                        tempDevice['targetValue'] = mod.targetValue;
                        tempDevice['isAlarmActive'] = mod.isAlarmActive;
                        tempDevice['notes'] = deviceNotes;
                        // if device is perin patch, update start time, frequency, and interval as these can be unique to each modalityType
                        // with other devices, the entire device + all its modalities have common start time, frequency, and interval values
                        if (device.deviceId === 1) {
                            tempDevice['startTime'] = mod.startTime;
                            tempDevice['frequency'] = mod.frequency;
                            tempDevice['interval'] = mod.interval;
                            tempDevice['isContinuous'] = mod.isContinuous;
                        }
                        // if we're updating a previously created modality
                        if (templateModalities[device.deviceId] && templateModalities[device.deviceId].find(mod => mod.deviceModalityId === tempDevice.deviceModalityId)?.accountProgramDeviceId) {
                            // Get templateId from templateModalities

                            tempDevice['accountProgramDeviceId'] = templateModalities[device.deviceId].find(mod => mod.deviceModalityId === tempDevice.deviceModalityId).accountProgramDeviceId;
                        } else {
                            delete tempDevice['accountProgramDeviceId'];
                        }
                        delete tempDevice['templateId'];
                        delete tempDevice['macAddress'];


                        // add rules
                        let rules = [];
                        notifications.forEach((notification) => {
                            if (notification.modalityId === mod.modalityId) {
                                let tempNotification = { ...notification };
                                tempNotification["deviceId"] = device.deviceId;
                                delete tempNotification["triggerTimestamps"];
                                rules.push(tempNotification);
                            }
                        });
                        modalityFrequencies.forEach((modFrequency) => {
                            if (modFrequency.modalityId === mod.modalityId) {
                                let tempModFrequency = { ...modFrequency };
                                delete tempModFrequency["triggerTimestamps"];
                                tempModFrequency["deviceId"] = device.deviceId;
                                rules.push(tempModFrequency);
                            }
                        });
                        tempDevice['rules'] = rules;

                        tempDevices.push(tempDevice);

                    }
                })
                if (tempDevices.length >= 1) {
                    newDevices = newDevices.concat(tempDevices);
                } else {
                    newDevices.push({ ...device });
                }
            })
        }
        let newContents = [];
        if (contents.length >= 1) {
            newContents = contents.map((content) => {
                return {
                    accountProgramContentId: content.accountProgramContentId,
                    contentId: content.contentId,
                    interval: content.interval,
                    startTime: content.startTime,
                    frequency: String(content.frequency),
                    notes: contentNotes
                };
            });
        };
        let newQuestionnaires = [];
        if (questionnaires.length >= 1) {
            newQuestionnaires = questionnaires.map((q) => {
                const { templateId, ...rest } = q;
                return { ...rest, notes: questionnaireNotes };
            });
        };
        let newHubs = hubs && hubs.length > 0 && hubs[0].serialNumber ? hubs : [];

        // check if duration was filled out, if not set duration to 0
        const returnData = {
            duration: data.duration,
            name: data.name,
            description: data.description,
            groupId: data.groupId,
            patient: {
                id: data.patient.id,
                firstName: data.patient.firstName,
                lastName: data.patient.lastName,
            },
            enrollment: { 
                groups: formattedGroups, 
                startDate: data.enrollment.startDate,
                endDate: data.enrollment.endDate,
            },
            devices: newDevices,
            contents: newContents,
            questionnaires: newQuestionnaires,
            hubs: newHubs,
            duration: data.duration === "" ? 0 : data.duration
        };
        return returnData;
    };

    const handleSubmit = async () => {
        const url = updatingProgram ? `enrollment/${data.enrollmentId}` : 'enrollment';
        const newProgram = formatProgramData();
        const method = updatingProgram ? 'PUT' : 'POST';
        const alertMessage = updatingProgram ? 'updating' : addingProgram ? 'adding' : reenrollingProgram ? 're-enrolling' : 'enrolling';
        try {
            const response = await httpService.request({
                method: method,
                headers: { "Content-Type": "application/json" },
                url: url,
                data: JSON.stringify(newProgram),
            });
            if (response) {
                if (updatingProgram) {
                    setPage(0);
                } else {
                    navigate('/patient-enrollment');
                }
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Success ${alertMessage} patient`,
                        type: 'success'
                    })
                );
            }
        } catch (error) {
            console.log("error:" + error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: `Error ${alertMessage} patient`,
                    type: 'danger'
                })
            );
        }
    };

    const getErrorMessage = () => {
        if (page === 0 && errorMessages.program.showing) {
            return errorMessages.program.message;
        }
        if (page === 1 && errorMessages.device.showing) {
                return errorMessages.device.message;
        } else if (page === 3 && errorMessages.questionnaire.showing) {
            return errorMessages.questionnaire.message;
        } else if (page === 4 ) {
            if (errorMessages.content.contentShowing) {
                return errorMessages.content.contentMessage;
            } else if (errorMessages.content.programShowing) {
                return errorMessages.content.programMessage;
            }
            
        }
        return null;
    };

    const errorMessage = getErrorMessage();

    return (
        <div className={styles.MainCard} style={{ justifyContent: "start" }}>
            <div className={styles.ContentTitle}>{updatingProgram ? "Updating Patient Enrollment" : addingProgram ? "Add Patient Enrollment" : reenrollingProgram ? "Re-enroll in previous program" : "Patient Enrollment"}</div>
            <div  className={styles.ProgressBarContainer}>
                <ProgressBar page={page} navigateToStep={ProgressBarNavigation} getPageName={getPageName} totalSteps={6} />
            </div>
            <div className={styles.HideScrollContainer} style={{ width: '100%' }}>
                {PageDisplay()}
            </div>
            <div className={styles.ButtonsFooterContainerWithError}>
                {errorMessage && (
                    <div className="page-error">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                        {errorMessage}
                    </div>
                )}
               
                <div style={{ display: "flex", columnGap: "10px" }}>
                    {page !== 0 ? (
                        <button
                            className={styles.BackButton}
                            onClick={(e) => {
                                e.preventDefault();
                                if (page === 4) {
                                    prevPage();
                                    return;
                                }
                                if (checkPageValidity()) {
                                    return;
                                };
                                prevPage();
                            }}
                        >
                            Back
                        </button>)
                        : null}
                    <button
                        className={(page === 5 && (updatingProgram || !updatingProgram)) ? styles.UpdateButton : styles.NextButton}
                        style={{ marginRight: "10px" }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (checkPageValidity()) {
                                return;
                            };
                            if (page === 5) {
                                if (updatingProgram) {
                                    // Log Update button click
                                    const patientName = data.patient.firstName && data.patient.lastName ? `${data.patient.firstName} ${data.patient.lastName}` : 'Unknown Patient';
                                    const programName = data.name || 'Unknown Program';
                                    const customMessage = `Update - ${patientName} - ${programName}`;
                                    
                                    Logger.shared.info(
                                        LogElements.updateButton,
                                        LogAction.clicked,
                                        'EnrollPatient.js',
                                        customMessage
                                    );
                                } else if (!updatingProgram && !reenrollingProgram) {
                                    Logger.shared.info(
                                        LogElements.enrollPatientButton,
                                        LogAction.clicked,
                                        'EnrollPatient.js',
                                        'Finish'
                                    );
                                }
                                handleSubmit()
                            } else {
                                nextPage();
                            };
                        }}
                    >
                        {(page === 5 && updatingProgram) ? "Update" : (page === 5 && reenrollingProgram) ? "Re-enroll" : page === 5 ? "Enroll Patient" : "Next"}
                    </button>
                    {((updatingProgram && page !== 5) || (!updatingProgram && [1,2,3,4].includes(page))) && (
                        <button
                            style={{float:"right", marginLeft:"auto"}}
                            disabled={data.devices.length === 0 && data.questionnaires.length === 0 && data.contents.length === 0}
                            className={styles.UpdateButton}
                            onClick={(e) => {
                                e.preventDefault();
                                if (checkPageValidity()) {
                                    return;
                                };
                                if (updatingProgram) {
                                    // Log Update button click
                                    const patientName = data.patient.firstName && data.patient.lastName ? `${data.patient.firstName} ${data.patient.lastName}` : 'Unknown Patient';
                                    const programName = data.name || 'Unknown Program';
                                    const customMessage = `Update - ${patientName} - ${programName}`;
                                    
                                    Logger.shared.info(
                                        LogElements.updateButton,
                                        LogAction.clicked,
                                        'EnrollPatient.js',
                                        customMessage
                                    );
                                } else if (!updatingProgram) {
                                    Logger.shared.info(
                                        LogElements.enrollPatientButton,
                                        LogAction.clicked,
                                        'EnrollPatient.js',
                                        'Finish'
                                    );
                                }
                                handleSubmit();
                            }}
                        >
                            {updatingProgram ? "Update" : "Enroll Patient"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

}

export default EnrollPatient;
